
import { useState } from 'react'
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Box, IconButton } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';

// import { useGeneralAppState } from '../../generalAppState';
import { useAppState } from '../../reduxish/appState';
import CasinoSortEnum from '../../models/CasinoSortEnum';
import SortDropDown from '../SortDropDown';
import HomeFavoritesToggle from './HomeFavoritesToggle';
import { useRefreshSlot } from './RefreshSlotContext';

// Hide the browser's native ::-webkit-search-cancel-button (whose style we
// can't theme cleanly) and rely on our own MUI ClearIcon adornment so the
// clear control matches the rest of the toolbar icons.
const searchInputSx = {
    '& input[type="search"]::-webkit-search-cancel-button': {
        WebkitAppearance: 'none',
        appearance: 'none',
    },
    '& input[type="search"]::-webkit-search-decoration': {
        WebkitAppearance: 'none',
        appearance: 'none',
    },
};


const UNAUTH_MSG = 'Search our full casino list, sort by table minimums, and save your favorites. Sign in to unlock it all!'

export default function CasinoSearchAndSort() {
    const { state: { casinoSearch: { searchText }, isOnMobileDevice, auth }, dispatch } = useAppState();
    const { setSlot } = useRefreshSlot();
    const isAuthenticated = auth?.isAuthenticated ?? false

    const handleUnauthClick = () => {
        dispatch({ type: 'OK_DIALOG_OPEN_FULL_UPDATE', open: true, title: 'Sign in to continue', text: UNAUTH_MSG })
    }

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: 'CASINO_SEARCH_TEXT_UPDATE', searchText: e.target.value })
    };

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
    };

    const onClear = () => {
        dispatch({ type: 'CASINO_SEARCH_TEXT_UPDATE', searchText: '' })
    };

    const clearAdornment = searchText
        ? (
            <InputAdornment position="end">
                <IconButton
                    aria-label="clear search"
                    onClick={onClear}
                    size="small"
                    edge="end"
                    sx={{ color: 'action.active' }}
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            </InputAdornment>
        )
        : null;

    return (isOnMobileDevice ?
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
            <Box sx={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex' }}>
                <Paper
                    component="form"
                    onSubmit={onSubmit}
                    sx={{ p: "2px 4px", marginLeft: "4px", display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}
                >
                    <TextField
                        variant="standard"
                        type="search"
                        sx={{ ml: 1, flex: 1, ...searchInputSx }}
                        placeholder="Search"
                        slotProps={{
                            htmlInput: { "aria-label": "search" },
                            input: {
                                disableUnderline: true,
                                endAdornment: clearAdornment,
                            },
                        }}
                        value={searchText}
                        onChange={onChange}
                    />
                    <Divider sx={{ m: 0.5 }} orientation="vertical" flexItem />
                    <SortDropDown />
                    <Divider sx={{ m: 0.5 }} orientation="vertical" flexItem />
                    <HomeFavoritesToggle variant="icon" />
                </Paper>
                {!isAuthenticated && (
                    <Box
                        onClick={handleUnauthClick}
                        sx={{ position: 'absolute', inset: 0, zIndex: 9999, cursor: 'pointer' }}
                    />
                )}
            </Box>
            <Box ref={setSlot} sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5, mr: 1 }} />
        </Box>
        :
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Paper sx={{
                    flex: 1,
                    borderRadius: "0",
                    boxShadow: "0px 2px 0px 0px rgba(0, 0, 0, 0.2), 0px 1px 0px 0px rgba(0, 0, 0, 0.14), 0px 1px 0px 0px rgba(0, 0, 0, 0.12)"
                }}>
                    <Stack
                        component="form"
                        onSubmit={onSubmit}
                        direction="row"
                        sx={{ justifyContent: 'end', alignItems: 'center' }}
                    >
                        <TextField
                            variant="standard"
                            sx={{ flexGrow: 0, width: { md: "350px", xs: "60%" }, border: "0px", ...searchInputSx }}
                            type="search"
                            placeholder="Search"
                            slotProps={{
                                htmlInput: { "aria-label": "search" },
                                input: {
                                    disableUnderline: true,
                                    endAdornment: clearAdornment,
                                },
                            }}
                            value={searchText}
                            onChange={onChange}
                        />
                        <IconButton type="submit" sx={{ p: "10px" }} aria-label="search">
                            <SearchIcon />
                        </IconButton>

                        <Divider sx={{ m: 1 }} orientation="vertical" variant="middle" flexItem />

                        <SortDropDown />
                    </Stack>
                </Paper>
                <HomeFavoritesToggle />
                {!isAuthenticated && (
                    <Box
                        onClick={handleUnauthClick}
                        sx={{ position: 'absolute', inset: 0, zIndex: 9999, cursor: 'pointer' }}
                    />
                )}
            </Box>
            <Box ref={setSlot} sx={{ display: 'inline-flex', alignItems: 'center', mx: 1 }} />
        </Box>
    )
}
